

import { useState, useEffect, useCallback, useRef } from 'react';
import { projectRepository } from '@/features/projects/repository';
import { ProjectWithZones } from '@/features/projects/types';

interface UsePaginatedProjectsOptions {
  pageSize?: number;
}

interface GetProjectsParams {
  status?: string | string[];
  name?: string;
  address?: string;
  building_type?: string;
  start_date_from?: string;
  start_date_to?: string;
  end_date_from?: string;
  end_date_to?: string;
  area_min?: number;
  area_max?: number;
  include_zones?: boolean;
}

export const usePaginatedProjects = (
  params: GetProjectsParams = {},
  options: UsePaginatedProjectsOptions = {}
) => {
  const { pageSize = 10 } = options;
  const [projects, setProjects] = useState<ProjectWithZones[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // paramsを安定化するため、JSON文字列で依存関係を管理
  const paramsKey = JSON.stringify(params);
  const lastParamsRef = useRef<string>('');
  const retryCountRef = useRef<number>(0);
  const maxRetries = 3;

  // データを取得する関数
  const fetchProjects = useCallback(async () => {
    // 同じparamsでの連続実行を防止
    if (lastParamsRef.current === paramsKey && retryCountRef.current >= maxRetries) {
      console.warn('[usePaginatedProjects] Max retries reached, stopping');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const apiParams = {
        tenant_id: undefined, // バックエンドで認証されたユーザーのテナントIDを使用
        status: Array.isArray(params.status) ? params.status : params.status,
        building_type: params.building_type,
        name: params.name,
        start_date_from: params.start_date_from,
        start_date_to: params.start_date_to,
        end_date_from: params.end_date_from,
        end_date_to: params.end_date_to,
        area_min: params.area_min,
        area_max: params.area_max,
        limit: pageSize,
        skip: (currentPage - 1) * pageSize,
        include_zones: params.include_zones,
      };

      const { items, total } = await projectRepository.getProjects(apiParams);
      setProjects(items);
      setTotal(total);

      // 成功時はリトライカウントをリセット
      retryCountRef.current = 0;
      lastParamsRef.current = paramsKey;

    } catch (err: unknown) {
      console.error('Failed to fetch projects:', err);

      // 接続エラーの場合はリトライカウントを増加
      if (err instanceof Error && ((err as any).code === 'ECONNREFUSED' || err.message?.includes('Network Error'))) {
        retryCountRef.current += 1;
        if (retryCountRef.current >= maxRetries) {
          setError('サーバーへの接続に失敗しました。しばらく待ってから再試行してください。');
        } else {
          // 指数バックオフでリトライ
          setTimeout(() => {
            fetchProjects();
          }, Math.pow(2, retryCountRef.current) * 1000);
          return;
        }
      } else {
        retryCountRef.current = 0; // 接続エラー以外の場合はリセット
        const errorMessage = err instanceof Error && 'response' in err && err.response && typeof err.response === 'object' && 'data' in err.response && err.response.data && typeof err.response.data === 'object' && 'message' in err.response.data && typeof err.response.data.message === 'string'
          ? err.response.data.message
          : 'プロジェクトデータの取得に失敗しました';
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [paramsKey, pageSize, currentPage, params.area_max, params.area_min, params.building_type, params.end_date_from, params.end_date_to, params.name, params.start_date_from, params.start_date_to, params.status]); // すべての依存関係を明示的に指定

  // paramsが変更された時のみ実行
  useEffect(() => {
    // 新しいparamsの場合はリトライカウントをリセット
    if (lastParamsRef.current !== paramsKey) {
      retryCountRef.current = 0;
    }
    fetchProjects();
  }, [fetchProjects, paramsKey]); // paramsKeyを依存関係に追加

  return {
    projects,
    total,
    isLoading,
    error,
    pagination: {
      totalItems: total,
      pageSize,
      currentPage,
      totalPages: Math.ceil(total / pageSize)
    },
    setCurrentPage,
    mutate: async () => {
      await fetchProjects();
    }, // プロジェクトデータの再取得関数

    cacheKey: `api-projects-${paramsKey}`, // 互換性のため
  };
};
